import React from 'react'
import axios from 'axios'
import {
  Drawer,
  Typography,
  IconButton,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Tooltip,
  Collapse
} from '@material-ui/core'
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@material-ui/icons'

const DRAWER_WIDTH = 480

// ── Rule Builder Mode ────────────────────────────

const RuleBuilder = ({ url, projectId, docId, page, pageImage: pageImageProp, extractedItems: itemsProp, discipline, onRuleSaved, onCancel }) => {
  const [feedback, setFeedback] = React.useState('')
  const [drafting, setDrafting] = React.useState(false)
  const [draftResult, setDraftResult] = React.useState(null)
  const [title, setTitle] = React.useState('')
  const [rule, setRule] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const [pageImage, setPageImage] = React.useState(pageImageProp || null)
  const [extractedItems, setExtractedItems] = React.useState(itemsProp || null)

  // Fetch page image + items on mount if not provided as props
  React.useEffect(() => {
    if (!projectId || !docId || !page) return
    if (!pageImage) {
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/page-image/${page}`)
        .then(res => setPageImage({ url: res.data.url }))
        .catch(() => {})
    }
    if (!extractedItems) {
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/parse-result`)
        .then(res => {
          const items = []
          const sheets = (res.data.parseResult && res.data.parseResult.sheets) || []
          for (const sheet of sheets) {
            for (const table of (sheet.tables || [])) {
              for (const row of (table.rows || [])) {
                if (row.page === Number(page)) items.push(row)
              }
            }
          }
          setExtractedItems(items)
        })
        .catch(() => setExtractedItems([]))
    }
  }, [projectId, docId, page]) // eslint-disable-line

  const handleDraft = async () => {
    if (!feedback.trim()) return
    setDrafting(true)
    setError('')
    try {
      const res = await axios.post(
        `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/draft-rule`,
        { page, userFeedback: feedback }
      )
      setDraftResult(res.data)
      setTitle(res.data.suggestedTitle || '')
      setRule(res.data.suggestedRule || '')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to draft rule')
    }
    setDrafting(false)
  }

  const handleSave = async () => {
    if (!title.trim() || !rule.trim()) return
    setSaving(true)
    setError('')
    try {
      await axios.post(`${url}/api/bid-estimation/extraction-rules`, {
        title,
        rule,
        origin: { projectId, documentId: docId, page, pageImageS3Key: pageImage?.s3Key }
      })
      if (onRuleSaved) onRuleSaved()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save rule')
    }
    setSaving(false)
  }

  return (
    <div style={{ padding: 16 }}>
      <Typography variant="subtitle2" gutterBottom>
        Improve Extraction — Page {page} ({discipline || 'unknown'})
      </Typography>

      {pageImage && (
        <div style={{ marginBottom: 12, borderRadius: 4, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <img
            src={pageImage.url}
            alt={`Page ${page}`}
            style={{ width: '100%', maxHeight: 200, objectFit: 'contain', backgroundColor: '#f5f5f5' }}
          />
        </div>
      )}

      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
        {extractedItems?.length || 0} items currently extracted
      </Typography>

      <TextField
        label="What's wrong with these results?"
        placeholder="e.g., These should be trees not groundcover — see the container size column"
        multiline
        rows={3}
        fullWidth
        variant="outlined"
        size="small"
        value={feedback}
        onChange={e => setFeedback(e.target.value)}
        disabled={drafting || saving}
        style={{ marginBottom: 12 }}
      />

      {!draftResult && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleDraft}
          disabled={!feedback.trim() || drafting}
          startIcon={drafting ? <CircularProgress size={16} color="inherit" /> : null}
          style={{ textTransform: 'none', marginBottom: 12 }}
          fullWidth
        >
          {drafting ? 'Drafting Rule…' : 'Draft Rule'}
        </Button>
      )}

      {draftResult && (
        <div style={{ backgroundColor: '#f5f5f5', borderRadius: 4, padding: 12, marginBottom: 12 }}>
          <TextField
            label="Rule Title"
            fullWidth
            variant="outlined"
            size="small"
            value={title}
            onChange={e => setTitle(e.target.value)}
            disabled={saving}
            style={{ marginBottom: 8 }}
          />
          <TextField
            label="Rule Text"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            size="small"
            value={rule}
            onChange={e => setRule(e.target.value)}
            disabled={saving}
            inputProps={{ maxLength: 500 }}
            helperText={`${rule.length}/500`}
            style={{ marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!title.trim() || !rule.trim() || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              style={{ textTransform: 'none', flex: 1 }}
            >
              {saving ? 'Saving…' : 'Save Rule'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setDraftResult(null); setTitle(''); setRule('') }}
              disabled={saving}
              style={{ textTransform: 'none' }}
            >
              Re-draft
            </Button>
          </div>
        </div>
      )}

      {error && (
        <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
          {error}
        </Typography>
      )}

      <Button onClick={onCancel} style={{ textTransform: 'none', marginTop: 8 }} fullWidth>
        Cancel
      </Button>
    </div>
  )
}

// ── Rules Manager Mode ────────────────────────────

const RuleCard = ({ rule, url, onUpdated }) => {
  const [expanded, setExpanded] = React.useState(false)
  const [changeLog, setChangeLog] = React.useState(null)
  const [editing, setEditing] = React.useState(false)
  const [editTitle, setEditTitle] = React.useState(rule.title)
  const [editRule, setEditRule] = React.useState(rule.rule)
  const [saving, setSaving] = React.useState(false)

  const fetchChangeLog = async () => {
    if (changeLog) return
    try {
      const res = await axios.get(`${url}/api/bid-estimation/extraction-rules/${rule._id}`)
      setChangeLog(res.data.changeLog || [])
    } catch (err) {
      console.error('Failed to fetch change log:', err)
      setChangeLog([])
    }
  }

  const handleToggle = async () => {
    try {
      await axios.patch(`${url}/api/bid-estimation/extraction-rules/${rule._id}/toggle`)
      if (onUpdated) onUpdated()
    } catch (err) {
      console.error('Toggle failed:', err)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await axios.put(`${url}/api/bid-estimation/extraction-rules/${rule._id}`, {
        title: editTitle,
        rule: editRule
      })
      setEditing(false)
      if (onUpdated) onUpdated()
    } catch (err) {
      console.error('Save failed:', err)
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this rule?')) return
    try {
      await axios.delete(`${url}/api/bid-estimation/extraction-rules/${rule._id}`)
      if (onUpdated) onUpdated()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 4,
      padding: 12,
      marginBottom: 8
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FormControlLabel
          control={<Switch size="small" checked={rule.active} onChange={handleToggle} color="primary" />}
          label=""
          style={{ margin: 0 }}
        />
        <div style={{ flex: 1 }}>
          {editing ? (
            <TextField size="small" fullWidth value={editTitle} onChange={e => setEditTitle(e.target.value)} variant="outlined" />
          ) : (
            <Typography variant="body2" style={{ fontWeight: 600 }}>{rule.title}</Typography>
          )}
        </div>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => setEditing(!editing)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={handleDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {editing ? (
        <div style={{ marginTop: 8 }}>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            value={editRule}
            onChange={e => setEditRule(e.target.value)}
            variant="outlined"
            inputProps={{ maxLength: 500 }}
            helperText={`${editRule.length}/500`}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button size="small" variant="contained" color="primary" onClick={handleSave} disabled={saving} style={{ textTransform: 'none' }}>
              Save
            </Button>
            <Button size="small" onClick={() => { setEditing(false); setEditTitle(rule.title); setEditRule(rule.rule) }} style={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, fontSize: 12 }}>
          {rule.rule}
        </Typography>
      )}

      {rule.origin && rule.origin.page && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
          Origin: Page {rule.origin.page}
        </Typography>
      )}

      <div
        onClick={() => { setExpanded(!expanded); if (!expanded) fetchChangeLog() }}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginTop: 4 }}
      >
        <Typography variant="caption" color="textSecondary">History</Typography>
        {expanded ? <ExpandLessIcon style={{ fontSize: 14 }} /> : <ExpandMoreIcon style={{ fontSize: 14 }} />}
      </div>
      <Collapse in={expanded}>
        {!changeLog && <CircularProgress size={14} style={{ margin: 4 }} />}
        {(changeLog || []).map((entry, i) => (
          <div key={i} style={{ paddingLeft: 8, borderLeft: '2px solid #e0e0e0', marginTop: 4 }}>
            <Typography variant="caption" style={{ display: 'block' }}>
              <strong>{entry.action}</strong> — {entry.changedByName || 'Unknown'} — {new Date(entry.changedAt).toLocaleDateString()}
            </Typography>
            {entry.reason && (
              <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                Reason: {entry.reason}
              </Typography>
            )}
          </div>
        ))}
      </Collapse>
    </div>
  )
}

const RulesManager = ({ url }) => {
  const [rules, setRules] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  const fetchRules = React.useCallback(async () => {
    try {
      const res = await axios.get(`${url}/api/bid-estimation/extraction-rules`)
      setRules(res.data)
    } catch (err) {
      console.error('Failed to load rules:', err)
    }
    setLoading(false)
  }, [url])

  React.useEffect(() => {
    fetchRules()
  }, [fetchRules])

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={24} /></div>
  }

  return (
    <div style={{ padding: 16 }}>
      <Typography variant="subtitle2" gutterBottom>
        Extraction Rules ({rules.length})
      </Typography>

      {rules.length === 0 && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, textAlign: 'center' }}>
          No extraction rules yet. Use "Improve Extraction" on a parsed page to create your first rule.
        </Typography>
      )}

      {rules.map(rule => (
        <RuleCard key={rule._id} rule={rule} url={url} onUpdated={fetchRules} />
      ))}
    </div>
  )
}

// ── Main Drawer ────────────────────────────

const ExtractionRulesDrawer = ({
  open,
  onClose,
  url,
  mode,            // 'builder' | 'manager'
  projectId,
  docId,
  page,
  pageImage,       // { url, s3Key }
  extractedItems,
  discipline
}) => {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div style={{ width: DRAWER_WIDTH }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {mode === 'builder' ? 'Improve Extraction' : 'Extraction Rules'}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {mode === 'builder' ? (
          <RuleBuilder
            url={url}
            projectId={projectId}
            docId={docId}
            page={page}
            pageImage={pageImage}
            extractedItems={extractedItems}
            discipline={discipline}
            onRuleSaved={onClose}
            onCancel={onClose}
          />
        ) : (
          <RulesManager url={url} onClose={onClose} />
        )}
      </div>
    </Drawer>
  )
}

export default ExtractionRulesDrawer
